import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Boxes, Calculator, PackageCheck, MapPin, IndianRupee, Layers, BarChart3, Zap, Globe2,
  ArrowRight, Package, Truck, Bell, Check, Building2, Store, Briefcase, User, Factory,
  Banknote, Eye, RefreshCcw, Link2, AlertTriangle, UserX, PhoneOff, CalendarClock, HelpCircle,
  Upload, Printer, TrendingUp, Headphones, CheckCircle2,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, GradientCta, fadeUp } from "../components/marketing/Blocks";

const FEATURES = [
  { n: "01", icon: Boxes, title: "Multi-Courier Shipping", desc: "Access multiple courier services from a single ShipAny platform.",
    points: ["Multiple courier options", "Centralized shipping", "Courier comparison", "Flexible courier selection", "Unified shipment management"] },
  { n: "02", icon: Calculator, title: "Smart Rate Comparison", desc: "Compare available courier options before booking.",
    points: ["Courier & shipping price", "Estimated delivery", "Serviceability", "COD availability", "Recommended option"], cta: ["Compare Rates", "/pricing#calculator"] },
  { n: "03", icon: PackageCheck, title: "Shipment Management", desc: "Manage the complete shipment lifecycle.",
    points: ["Create shipments", "Generate AWB", "Print shipping labels", "Download invoices", "Cancel shipments", "Shipment history", "Bulk shipments"] },
  { n: "04", icon: MapPin, title: "Real-Time Tracking", desc: "Track packages from pickup to delivery.",
    points: ["Order Created", "Pickup Scheduled", "Picked Up", "In Transit", "Destination Hub", "Out for Delivery", "Delivered"] },
  { n: "05", icon: IndianRupee, title: "COD Shipping", desc: "Simplify Cash on Delivery operations.",
    points: ["COD shipment creation", "COD amount management", "COD tracking", "Delivery status", "Settlement visibility", "COD reporting"] },
  { n: "06", icon: Layers, title: "Bulk Shipping", desc: "Create large numbers of shipments efficiently.",
    points: ["CSV upload", "Bulk shipment creation", "Bulk label generation", "Bulk AWB generation", "Bulk tracking", "Validation & error reporting"] },
  { n: "07", icon: BarChart3, title: "Analytics Dashboard", desc: "Show important logistics metrics.",
    points: ["Total Shipments", "Delivered", "In Transit", "RTO", "COD", "Shipping Spend", "Delivery Performance", "Courier Performance"] },
  { n: "08", icon: Zap, title: "Shipping Automation", desc: "Automate repetitive logistics operations.",
    points: ["Automated shipment creation", "Shipping labels", "Tracking updates", "Order synchronization", "Webhooks", "API automation"] },
  { n: "09", icon: Globe2, title: "International Shipping", desc: "Support cross-border shipping workflows.",
    points: ["Country selection", "Postal code & destination address", "Package dimensions", "Actual / volumetric / billed weight", "Item details & declared value", "Customs information", "International tracking"] },
];

const AUDIENCES = [
  { icon: User, title: "Individuals", desc: "Simple shipping and tracking." },
  { icon: Store, title: "Small Businesses", desc: "Easy shipment management." },
  { icon: Briefcase, title: "D2C Brands", desc: "High-volume shipping and COD." },
  { icon: Building2, title: "E-commerce Sellers", desc: "Bulk shipments and analytics." },
  { icon: Factory, title: "Enterprises", desc: "API automation and custom workflows." },
];

const COD_FEATURES = [
  { icon: Banknote, title: "COD Shipment Booking", desc: "Create COD shipments quickly — choose COD at booking and set the collection amount." },
  { icon: Eye, title: "COD Visibility", desc: "Monitor COD shipments from a centralized dashboard." },
  { icon: CheckCircle2, title: "Delivery Status", desc: "Know whether COD orders are delivered, attempted or returned." },
  { icon: RefreshCcw, title: "COD Reconciliation", desc: "Organize COD-related shipment and payment information." },
  { icon: Link2, title: "Shipment-Level Tracking", desc: "Connect shipment status with COD information." },
];
const COD_FLOW = ["Customer Places COD Order", "Shipment Created", "Courier Pickup", "Out for Delivery", "COD Collected", "Delivered", "COD Reconciliation"];
const COD_STATS = ["Total COD Orders", "COD Delivered", "COD Pending", "COD Collected", "COD Reconciliation"];

const NDR_STATS = [["Total NDR", "bg-slate-400"], ["Open NDR", "bg-amber-400"], ["Action Required", "bg-[#FF9933]"], ["Resolved", "bg-emerald-400"], ["RTO Risk", "bg-red-400"]];
const NDR_REASONS = [
  { icon: UserX, title: "Customer Unavailable" }, { icon: MapPin, title: "Incorrect Address" },
  { icon: PhoneOff, title: "Customer Not Reachable" }, { icon: AlertTriangle, title: "Customer Refused" },
  { icon: CalendarClock, title: "Delivery Rescheduled" }, { icon: HelpCircle, title: "Other Delivery Issues" },
];
const NDR_FLOW = ["NDR Detected", "Reason Identified", "Customer Action", "Delivery Reattempt", "Delivered"];
const NDR_FEATURES = [
  { icon: Layers, title: "Centralized NDR View", desc: "Manage NDR shipments from one dashboard." },
  { icon: Zap, title: "Faster Action", desc: "Identify shipments that need immediate attention." },
  { icon: Headphones, title: "Customer Follow-Up", desc: "Support faster communication and resolution." },
  { icon: RefreshCcw, title: "Reattempt Visibility", desc: "Monitor delivery reattempt status." },
  { icon: TrendingUp, title: "RTO Prevention", desc: "Help reduce avoidable returns." },
];

const BULK_FEATURES = [
  { icon: Upload, title: "Bulk Order Upload", desc: "Upload multiple orders using CSV/Excel templates." },
  { icon: Layers, title: "Bulk Shipment Creation", desc: "Create many shipments in one operation." },
  { icon: Printer, title: "Bulk Label Generation", desc: "Generate labels for multiple shipments." },
  { icon: MapPin, title: "Bulk Tracking", desc: "Track large shipment volumes from one dashboard." },
  { icon: CheckCircle2, title: "Error Validation", desc: "Identify incorrect or incomplete order information before shipment creation." },
  { icon: Zap, title: "Operational Efficiency", desc: "Reduce repetitive manual work." },
];
const BULK_FLOW = ["Download Template", "Add Orders", "Upload File", "Validate Orders", "Create Shipments", "Generate Labels", "Schedule Pickup", "Track Shipments"];
const BULK_PIPELINE = ["Orders", "Processing", "Courier Assignment", "Labels", "Pickup"];

function FlowChips({ steps, testId, danger }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2" data-testid={testId}>
      {steps.map((s, i) => (
        <span key={s} className="flex items-center gap-2">
          <span className={`rounded-xl border px-4 py-2 text-xs font-semibold ${danger ? "border-red-400/40 bg-red-500/10 text-red-300" : "border-shipany-cyan/30 bg-shipany-cyan/10 text-slate-200"}`}
            data-testid={`${testId}-step-${i + 1}`}>{s}</span>
          {i < steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-600" />}
        </span>
      ))}
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="relative mx-auto mt-12 aspect-[16/10] max-w-lg sm:aspect-[16/9]" data-testid="features-hero-visual">
      <div className="absolute inset-0 flex items-center justify-center">
        <img src="/maps/india.svg" alt="India map" className="h-full w-auto opacity-70" />
      </div>
      {/* route lines */}
      <svg viewBox="0 0 100 62" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <path d="M 20 40 Q 35 18 52 30 T 80 22" fill="none" stroke="#12C8F5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" />
        <path d="M 24 44 Q 40 50 58 42 T 78 34" fill="none" stroke="#FF9933" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" style={{ animationDuration: "7s" }} />
      </svg>
      {/* floating package + truck */}
      <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-[8%] top-[18%] flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 shadow-xl backdrop-blur-xl">
        <Package className="h-6 w-6 text-[#FF9933]" />
      </motion.div>
      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[16%] right-[10%] flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 shadow-xl backdrop-blur-xl">
        <Truck className="h-6 w-6 text-[#10B981]" />
      </motion.div>
      {/* destination pins */}
      {[[30, 30], [58, 24], [70, 44], [42, 52]].map(([x, y], i) => (
        <span key={i} className="pulse-dot absolute h-2.5 w-2.5 rounded-full bg-[#FF9933]" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.4}s` }} />
      ))}
      {/* dashboard card */}
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[2%] top-[6%] w-44 rounded-2xl border border-white/15 bg-[#071B3A]/90 p-3.5 shadow-2xl backdrop-blur-xl">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Live dashboard</p>
        <div className="mt-2 space-y-1.5">
          {[["Rate saved", "text-emerald-400"], ["AWB assigned", "text-shipany-cyan"], ["Pickup today", "text-slate-300"]].map(([t, c]) => (
            <div key={t} className="flex items-center gap-1.5 text-[10px] font-semibold"><Check className={`h-3 w-3 ${c}`} /><span className="text-slate-200">{t}</span></div>
          ))}
        </div>
      </motion.div>
      {/* tracking notification */}
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[6%] left-[2%] flex w-48 items-center gap-2.5 rounded-2xl border border-white/15 bg-[#071B3A]/90 p-3 shadow-2xl backdrop-blur-xl">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#10B981]/20"><Bell className="h-4 w-4 text-[#10B981]" /></span>
        <div><p className="text-[10px] font-bold text-white">Out for delivery</p><p className="text-[9px] text-slate-400">Arriving today by 7 PM</p></div>
      </motion.div>
    </div>
  );
}

export default function Features() {
  return (
    <MarketingLayout>
      <div data-testid="features-page">
        <MarketingHero kicker="Platform" title="Everything You Need to Ship Smarter"
          sub="Powerful shipping tools designed to help businesses compare courier services, automate shipments, track deliveries and manage logistics from one platform.">
          <motion.div {...fadeUp} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup" data-testid="features-start-shipping-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Start Shipping <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/track-shipment" data-testid="features-track-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
              Track Shipment <MapPin className="h-4 w-4" />
            </Link>
          </motion.div>
          <HeroVisual />
        </MarketingHero>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="features-grid-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Capabilities" title="Powerful Features. One Shipping Platform." />
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f, i) => (
                <motion.div key={f.n} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`feature-card-${f.n}`}
                  className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40 hover:shadow-[0_18px_44px_rgba(3,11,24,0.6)]">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20 transition-transform duration-300 group-hover:scale-110">
                      <f.icon className="h-5 w-5 text-shipany-cyan" />
                    </span>
                    <span className="mono text-xs font-bold text-slate-600">{f.n}</span>
                  </div>
                  <h3 className="font-display mt-4 text-lg font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{f.desc}</p>
                  <ul className="mt-4 flex-1 space-y-1.5">
                    {f.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-xs text-slate-300"><Check className="h-3.5 w-3.5 shrink-0 text-[#10B981]" />{p}</li>
                    ))}
                  </ul>
                  {f.cta && (
                    <Link to={f.cta[1]} data-testid="feature-compare-rates-btn"
                      className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-shipany-cyan/40 py-2.5 text-xs font-bold text-shipany-cyan transition-colors hover:bg-shipany-cyan/10">
                      {f.cta[0]} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="audience-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Who it's for" title="Built for Every Shipping Requirement" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {AUDIENCES.map((a, i) => (
                <motion.div key={a.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.07 }}
                  data-testid={`audience-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF9933]/50">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FF9933]/15"><a.icon className="h-5 w-5 text-[#FF9933]" /></span>
                  <h3 className="font-display mt-4 text-sm font-bold text-white">{a.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{a.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ COD ============ */}
        <section id="cod" className="scroll-mt-24 bg-[#071B3A] py-20 sm:py-24" data-testid="cod-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Cash on Delivery" title="Manage COD Shipments Without the Complexity"
              sub="ShipAny helps businesses manage COD shipments with better visibility across booking, delivery and payment-related information." />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {COD_FEATURES.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 5) * 0.06 }}
                  data-testid={`cod-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#10B981]/50">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/15"><f.icon className="h-5 w-5 text-[#10B981]" /></span>
                  <h3 className="font-display mt-3 text-xs font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.div {...fadeUp} className="mt-12">
              <FlowChips steps={COD_FLOW} testId="cod-flow" />
            </motion.div>
            <motion.div {...fadeUp} className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8" data-testid="cod-dashboard-mockup">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">COD dashboard preview — your live numbers appear in the app</p>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
                {COD_STATS.map((s, i) => (
                  <div key={s} data-testid={`cod-stat-${i + 1}`} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{s}</p>
                    <p className="mono mt-1.5 text-lg font-bold text-slate-600">—</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex h-24 items-end gap-2" data-testid="cod-chart">
                {[40, 65, 45, 80, 55, 90, 70, 60, 85, 75, 95, 68].map((h, i) => (
                  <motion.span key={i} initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.04 }}
                    className="flex-1 rounded-t bg-gradient-to-t from-[#10B981]/40 to-[#10B981]/80" />
                ))}
              </div>
            </motion.div>
            <motion.div {...fadeUp} className="mt-10 text-center">
              <p className="font-display text-xl font-bold text-white" data-testid="cod-cta-title">Take Control of Your COD Operations</p>
              <Link to="/signup" data-testid="cod-start-shipping-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
                Start Shipping <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ============ NDR ============ */}
        <section id="ndr" className="scroll-mt-24 bg-shipany-navy py-20 sm:py-24" data-testid="ndr-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="NDR Management" title="Turn Delivery Failures Into Successful Deliveries"
              sub="Identify Non-Delivery Reports, understand failed delivery reasons and take timely action to improve delivery success." />
            <motion.div {...fadeUp} className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8" data-testid="ndr-dashboard-mockup">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">NDR dashboard preview — your live numbers appear in the app</p>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
                {NDR_STATS.map(([s, dot], i) => (
                  <div key={s} data-testid={`ndr-stat-${i + 1}`} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />{s}
                    </p>
                    <p className="mono mt-1.5 text-lg font-bold text-slate-600">—</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <div className="mt-12 grid gap-4 grid-cols-2 lg:grid-cols-6">
              {NDR_REASONS.map((r, i) => (
                <motion.div key={r.title} {...fadeUp} transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                  data-testid={`ndr-reason-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center">
                  <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/15"><r.icon className="h-4 w-4 text-amber-400" /></span>
                  <p className="mt-2.5 text-[11px] font-bold text-white">{r.title}</p>
                </motion.div>
              ))}
            </div>
            <motion.div {...fadeUp} className="mt-12 space-y-4">
              <FlowChips steps={NDR_FLOW} testId="ndr-flow" />
              <div className="flex justify-center">
                <span className="rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-2 text-xs font-semibold text-red-300" data-testid="ndr-rto-risk-path">
                  NDR → No Resolution → RTO Risk
                </span>
              </div>
            </motion.div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {NDR_FEATURES.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 5) * 0.06 }}
                  data-testid={`ndr-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all duration-300 hover:-translate-y-1.5 hover:border-amber-400/50">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/15"><f.icon className="h-5 w-5 text-amber-400" /></span>
                  <h3 className="font-display mt-3 text-xs font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.div {...fadeUp} className="mt-10 text-center">
              <p className="font-display text-xl font-bold text-white" data-testid="ndr-cta-title">Reduce RTO. Improve Delivery Success.</p>
              <Link to="/signup" data-testid="ndr-start-shipping-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
                Start Shipping <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ============ BULK SHIPPING ============ */}
        <section id="bulk" className="scroll-mt-24 bg-[#071B3A] py-20 sm:py-24" data-testid="bulk-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Bulk Shipping" title="Ship Hundreds or Thousands of Orders Faster"
              sub="Process large shipment volumes efficiently with bulk order uploads, automated shipment creation and centralized tracking." />
            <div className="relative mx-auto mt-14 max-w-4xl" data-testid="bulk-pipeline">
              <div className="absolute left-0 right-0 top-1/2 hidden h-0.5 -translate-y-1/2 rounded bg-white/10 sm:block" />
              <motion.div animate={{ left: ["2%", "92%"] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
                className="absolute top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg border border-[#FF9933]/50 bg-[#071B3A] shadow-[0_6px_20px_rgba(255,153,51,0.35)] sm:flex"
                data-testid="bulk-pipeline-package">
                <Package className="h-3.5 w-3.5 text-[#FF9933]" />
              </motion.div>
              <div className="relative grid gap-3 sm:grid-cols-5">
                {BULK_PIPELINE.map((p, i) => (
                  <div key={p} data-testid={`bulk-pipeline-step-${i + 1}`}
                    className="z-[5] rounded-xl border border-white/15 bg-[#0B1E3D] px-3 py-2.5 text-center text-[11px] font-bold text-slate-200">{p}</div>
                ))}
              </div>
            </div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {BULK_FEATURES.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`bulk-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20"><f.icon className="h-5 w-5 text-shipany-cyan" /></span>
                  <h3 className="font-display mt-4 text-sm font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.div {...fadeUp} className="mt-12">
              <FlowChips steps={BULK_FLOW} testId="bulk-flow" />
            </motion.div>
            <motion.div {...fadeUp} className="mt-14" data-testid="bulk-scale-section">
              <h3 className="text-center font-display text-xl font-bold text-white">Designed for Businesses That Ship at Scale</h3>
              <div className="mt-8 grid gap-5 sm:grid-cols-3">
                {[["100+", "Orders processed in one bulk upload"], ["10+", "Courier options compared per order"], ["24/7", "Shipment visibility from one dashboard"]].map(([v, l], i) => (
                  <div key={l} data-testid={`bulk-counter-${i + 1}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                    <p className="font-display text-3xl font-extrabold text-[#FF9933]">{v}</p>
                    <p className="mt-1.5 text-xs text-slate-400">{l}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div {...fadeUp} className="mt-12 text-center">
              <p className="font-display text-xl font-bold text-white" data-testid="bulk-cta-title">Ready to Scale Your Shipping?</p>
              <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link to="/signup" data-testid="bulk-start-shipping-btn"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
                  Start Shipping <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" data-testid="bulk-talk-sales-btn"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
                  Talk to Sales
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <GradientCta title="Ready to Ship Smarter?" sub="Bring your shipping operations together with ShipAny."
          primary={["Start Shipping", "/signup"]} secondary={["Talk to Sales", "/contact"]} testId="features-final-cta" />
      </div>
    </MarketingLayout>
  );
}
